import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import { PerceptronMarkLoop } from "@perceptron/ui/components/animations/perceptron-mark-loop";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";

import { ShimmerText } from "@demo/components/ToolCall/ShimmerText";

/**
 * Props interface for ReasoningSection.
 *
 * The model's thinking: open with a shimmering "Thinking for" label while it streams, then collapsed
 * behind "Thought for", which opens and closes it.
 */
export interface ReasoningSectionProps {
  children: React.ReactNode;
  isStreaming: boolean;
  /**
   * Keep the section open although thinking has finished: a tool call is running inside it. A call can
   * start after the answer has begun (a preamble, then a search), and it is anchored in the thinking; a
   * folded section would hide its progress.
   */
  holdOpen?: boolean;
  /** What a running tool call is doing; shown in place of the label while it runs. */
  activity?: string;
  /**
   * Told when the section starts folding away on its own (thinking done) and when the fold has finished, so
   * what follows can wait: text streaming in below a collapsing section, and scrolling to follow it, fight it.
   */
  onFoldingChange?: (folding: boolean) => void;
}

/** After thinking ends, the label settles into "Thought for" for this long before the body folds away. */
const FOLD_HOLD_MS = 200;
/**
 * How far through the fold what waits on it is released. The fold eases out (fast, then a long gentle
 * settle), so by now it is about nine tenths closed; waiting for its very end left a visible pause.
 */
const FOLD_RELEASE_AT = 0.6;
/** The fold takes longer the more there is to fold, within these bounds, so a long thought does not snap shut. */
const FOLD_MIN_MS = 450;
const FOLD_MAX_MS = 900;
/** Starts quickly and eases a long way into rest, so the end of the fold is gentle. */
const FOLD_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

/** How long the automatic fold takes for a body this tall. */
export const foldDuration = (height: number): number =>
  Math.round(Math.min(FOLD_MAX_MS, Math.max(FOLD_MIN_MS, 300 + height * 0.5)));

const ReasoningSection: React.FC<ReasoningSectionProps> = ({
  children,
  isStreaming,
  holdOpen = false,
  activity,
  onFoldingChange,
}) => {
  // Open while thinking streams or a call inside it runs, closed otherwise; the chevron toggles it either way.
  const shouldBeOpen = isStreaming || holdOpen;
  const [isOpen, setIsOpen] = useState(shouldBeOpen);
  const [duration, setDuration] = useState<number>(0);
  const [streamingDuration, setStreamingDuration] = useState<number>(0);
  const startTimeRef = useRef<number | null>(null);
  const wasOpenByRun = useRef(shouldBeOpen);
  const bodyRef = useRef<HTMLDivElement>(null);
  const onFoldingChangeRef = useRef(onFoldingChange);
  onFoldingChangeRef.current = onFoldingChange;
  const isFolding = useRef(false);
  // Rendered copy of the same: the header stays pinned until the fold is over, not just while thinking.
  const [isFoldingAway, setIsFoldingAway] = useState(false);
  const endFold = useCallback(() => {
    if (!isFolding.current) return;
    isFolding.current = false;
    setIsFoldingAway(false);
    onFoldingChangeRef.current?.(false);
  }, []);
  // The timing of the fold when thinking finishes; a click opens and closes at the usual speed.
  const [fold, setFold] = useState<React.CSSProperties | undefined>(undefined);

  // Thinking (or a call inside it) starting opens the section; once neither is going it folds away in
  // place, after a moment to settle. A layout effect, so what waits on the fold hears of it before the next
  // paint rather than a frame late.
  useLayoutEffect(() => {
    if (wasOpenByRun.current === shouldBeOpen) return;
    wasOpenByRun.current = shouldBeOpen;
    if (shouldBeOpen) {
      setFold(undefined);
      setIsOpen(true);
      return;
    }
    const duration = foldDuration(bodyRef.current?.offsetHeight ?? 0);
    setFold({
      transitionDuration: `${duration}ms`,
      transitionDelay: `${FOLD_HOLD_MS}ms`,
      transitionTimingFunction: FOLD_EASING,
    });
    setIsOpen(false);
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    isFolding.current = true;
    setIsFoldingAway(true);
    onFoldingChangeRef.current?.(true);
    // Released once the fold is mostly closed (FOLD_RELEASE_AT); its transitionend also ends it, should the
    // timer run late (a background tab).
    const timer = setTimeout(() => endFold(), FOLD_HOLD_MS + duration * FOLD_RELEASE_AT);
    // Reopened or unmounted mid-fold: the fold is over either way, so nothing waits on it.
    return () => {
      clearTimeout(timer);
      endFold();
    };
  }, [shouldBeOpen, endFold]);

  // Track streaming duration with interval
  useEffect(() => {
    if (isStreaming) {
      // Initialize start time if not already set
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      // Create interval to update streaming duration
      const interval = setInterval(() => {
        if (startTimeRef.current) {
          setStreamingDuration(Date.now() - startTimeRef.current);
        }
      }, 100);

      // Cleanup runs when isStreaming changes or unmount
      return () => clearInterval(interval);
    } else {
      // Streaming ended - calculate final duration
      if (startTimeRef.current) {
        const finalDuration = Date.now() - startTimeRef.current;
        setDuration(finalDuration);
        startTimeRef.current = null; // Reset for next session
      }
      setStreamingDuration(0);
    }
  }, [isStreaming]);

  const formattedDuration = useMemo(() => {
    if (duration < 100) return "< 0.1s";
    const seconds = duration / 1000;
    return `${seconds.toFixed(1)}s`;
  }, [duration]);

  const formattedStreamingDuration = useMemo(() => {
    const seconds = Math.floor(streamingDuration / 1000);
    return `${seconds}s`;
  }, [streamingDuration]);

  const handleToggle = useCallback(() => {
    setFold(undefined);
    setIsOpen((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setFold(undefined);
      setIsOpen((prev) => !prev);
    }
  }, []);

  // Don't render if content is empty
  if (!children) {
    return null;
  }

  const label =
    activity ??
    (isStreaming
      ? formatString(strings.thinkAnnotation.streamingLabel, formattedStreamingDuration)
      : formatString(strings.thinkAnnotation.completedLabel, formattedDuration));
  // Working: thinking streams, or a call inside it runs.
  const isWorking = isStreaming || !!activity;

  // One header and one body for the whole life of the section, so finishing changes them in place: the
  // shimmering label winds down into "Thought for", and the body collapses rather than being swapped out.
  return (
    <div className="mb-4" data-streaming={isStreaming || undefined} data-testid="reasoning-section">
      {/* While thinking streams, and until it has folded away, pinned to the top of the output box so the timer
          stays in view as the thinking scrolls under it; the fade below lets the text dissolve into it. It reaches
          up over the box's top padding (-mt-4 pt-4, stuck at -top-4), or text would show through that strip. Once
          the thinking is done it scrolls with the rest, and the fade goes with the pinning so it never covers an
          opened thought. */}
      <div
        className={cn(
          "relative -mt-4 bg-black pt-4",
          (isStreaming || isFoldingAway) &&
            "sticky -top-4 z-10 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-4 after:bg-gradient-to-b after:from-black after:to-transparent",
        )}
        data-testid="reasoning-header"
      >
        <button
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? strings.thinkAnnotation.ariaLabels.hideThoughtProcess
              : strings.thinkAnnotation.ariaLabels.showThoughtProcess
          }
          className="group relative flex min-w-0 max-w-full cursor-pointer items-center text-base"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
        >
          {/* Loops beside the label while working; when the work ends it fades out to the left and its space
              closes, so "Thought for" stands alone. */}
          <span
            aria-hidden
            className={cn(
              "flex shrink-0 overflow-hidden transition-[width,margin,opacity,transform] duration-300 ease-out motion-reduce:transition-none",
              isWorking ? "mr-1.5 w-[23px] translate-x-0 opacity-100" : "mr-0 w-0 -translate-x-2 opacity-0",
            )}
            data-testid="reasoning-mark"
          >
            <PerceptronMarkLoop className="h-3 w-[23px] shrink-0 text-foreground-secondary" playing={isWorking} />
          </span>
          <ShimmerText className="tool-call-text-soft truncate" isStreaming={isWorking} settledTone="secondary">
            {label}
          </ShimmerText>
          <Icon
            className={cn(
              "size-4 text-foreground-secondary transition-transform duration-300 ease-out motion-reduce:transition-none",
              isOpen ? "rotate-90" : "rotate-0",
            )}
            name="chevron-right"
          />
        </button>
      </div>

      {/* Content: its grid row grows from 0fr to 1fr, so opening and closing animate the real height, while the
          text fades. Closed, it is inert and hidden from assistive tech, as `hidden` made it before. */}
      <div
        aria-hidden={!isOpen || undefined}
        className={cn(
          "grid transition-[grid-template-rows,margin] duration-300 ease-out motion-reduce:transition-none",
          isOpen ? "mt-2 grid-rows-[1fr]" : "mt-0 grid-rows-[0fr]",
        )}
        data-testid="reasoning-content"
        inert={!isOpen}
        style={fold}
        onTransitionEnd={(event) => {
          if (event.target === event.currentTarget && event.propertyName === "grid-template-rows") endFold();
        }}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={cn(
              "border-l-2 border-foreground-secondary pl-4 text-foreground-secondary",
              "transition-opacity duration-300 ease-out motion-reduce:transition-none",
              isOpen ? "opacity-100" : "opacity-0",
            )}
            ref={bodyRef}
            style={fold}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReasoningSection;
